import React, { useEffect, useRef, useState } from 'react';
import { Copy, MessageCircle, Share2, X } from 'lucide-react';
import { getLessonPlan } from '../../api';
import { formatClock12, formatDateKeyLong } from '../adminDailySchedule/scheduleUtils';

// Visit dates are stored as UTC midnight of the day.
const formatDay = (value) => (value ? formatDateKeyLong(new Date(value).toISOString().slice(0, 10)) : '');

function Dialog({ title, onClose, children, footer, wide = false }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="lp-dialog-backdrop">
      <div
        ref={ref}
        className={`lp-dialog${wide ? ' is-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lp-dialog-title"
        tabIndex={-1}
      >
        <div className="lp-dialog-header">
          <h2 id="lp-dialog-title">{title}</h2>
          <button type="button" className="lp-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="lp-dialog-body">{children}</div>
        {footer && <div className="lp-dialog-footer">{footer}</div>}
      </div>
    </div>
  );
}

// "View" on a previous visit: its lesson plan when it had one.
export function PreviousVisitDialog({ visit, onClose }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visit.lessonPlanId) return undefined;
    let cancelled = false;
    getLessonPlan(visit.lessonPlanId)
      .then((response) => !cancelled && setPlan(response?.data?.plan || null))
      .catch(() => !cancelled && setError('Could not load the lesson plan of this visit.'));
    return () => {
      cancelled = true;
    };
  }, [visit.lessonPlanId]);

  return (
    <Dialog title={`Visit on ${formatDay(visit.date)}`} onClose={onClose} wide={Boolean(visit.lessonPlanId)}>
      <dl className="lp-facts">
        <div>
          <dt>Time</dt>
          <dd>{formatClock12(visit.startTime)} – {formatClock12(visit.endTime)}</dd>
        </div>
        <div>
          <dt>Coaches</dt>
          <dd>{visit.coaches.join(', ') || '-'}</dd>
        </div>
        <div>
          <dt>Key Focus</dt>
          <dd>{visit.keyFocus || '-'}</dd>
        </div>
      </dl>
      {!visit.lessonPlanId && <p className="lp-muted">No lesson plan was made for this visit.</p>}
      {visit.lessonPlanId && !plan && !error && <p className="lp-muted" role="status">Loading lesson plan...</p>}
      {error && <p className="lp-error" role="alert">{error}</p>}
      {plan && (
        <>
          <table className="lp-mini-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Child Name</th>
                <th scope="col">Computer</th>
                <th scope="col">One-on-One</th>
                <th scope="col">Special Instructions</th>
              </tr>
            </thead>
            <tbody>
              {plan.items.map((item, index) => (
                <tr key={item.studentId?._id || index}>
                  <td>{index + 1}</td>
                  <td>{item.studentId?.name || 'Child'}</td>
                  <td>{item.computer || '-'}</td>
                  <td>{item.oneOnOne || '-'}</td>
                  <td>{item.specialInstructions || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {plan.generalInstructions && (
            <p className="lp-note">
              <strong>General instructions:</strong> {plan.generalInstructions}
            </p>
          )}
          {plan.healthCheck && (
            <p className="lp-note">
              <strong>Health check:</strong> {plan.healthCheck}
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}

// After "Share Schedule": send the plan to the coaches.
export function ShareDialog({ title, text, onClose, onCopied, onCopyFailed }) {
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied();
    } catch (error) {
      onCopyFailed();
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title, text });
    } catch (error) {
      // Closing the device share sheet is not an error worth reporting.
    }
  };

  return (
    <Dialog
      title="Share Schedule"
      onClose={onClose}
      footer={
        <>
          <a
            className="lp-button lp-button-whatsapp"
            href={`https://wa.me/?text=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle size={18} aria-hidden="true" />
            WhatsApp
          </a>
          <button type="button" className="lp-button lp-button-outline" onClick={copy}>
            <Copy size={18} aria-hidden="true" />
            Copy text
          </button>
          {canShare && (
            <button type="button" className="lp-button lp-button-outline" onClick={share}>
              <Share2 size={18} aria-hidden="true" />
              More...
            </button>
          )}
        </>
      }
    >
      <p className="lp-muted">
        The visit is already on each coach's Daily Schedule. Send them the plan too:
      </p>
      <textarea className="lp-share-text" readOnly value={text} rows={12} aria-label="Lesson plan message" />
    </Dialog>
  );
}
